import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useMutation } from '@tanstack/react-query';
import { updateApproval } from '@/api/approvals';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { useProjectTeamMembers } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { useAppDispatch } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { UpdateApprovalPayload } from '@/types/Approvals';
import type { InternalTeam } from '@/types/Project';
import type { ApprovalAssignedUser } from '@/types/User';

type DelegateModalProps = {
  projectId: string;
  approvalId: number;
  assignedUser: ApprovalAssignedUser;
  title: string;
  onCancel?: () => void;
  onSubmitted?: () => void;
};

export function DelegateHookForm({ approvalId, assignedUser, projectId, onCancel, onSubmitted, title }: DelegateModalProps) {
  const dispatch = useAppDispatch();

  const teamMembersQuery = useProjectTeamMembers({
    projectId: Number(projectId),
  });

  const updateApprovalMutation = useMutation(async (payload: UpdateApprovalPayload) => updateApproval(payload), {
    onSuccess: () => {
      onSubmitted?.();
      onCancel?.();
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Failed to update Approval',
          toastType: 'failed',
        })
      );
    },
  });

  const teamMemberOptions = teamMembersQuery.data?.data.internal.filter((mem) => mem?.id !== assignedUser.id);

  const handleSubmit = ({ assignedUser, note }: { assignedUser: InternalTeam; note: string }) => {
    updateApprovalMutation.mutate({
      id: approvalId,
      status: 'DELEGATED',
      assignedUserId: assignedUser.id,
      note,
    });
  };

  return (
    <HookForm onSubmit={handleSubmit}>
      <p className='text-center'>Are you sure you want to delegate this {title} change order?</p>
      <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
      <div className='mt-5 space-y-5'>
        <HookReactSingleSelect
          label='Delegate To'
          name='assignedUser'
          required
          options={teamMemberOptions}
          getOptionLabel={(option) => (option as InternalTeam).name}
          getOptionValue={(option) => `${(option as InternalTeam).id}`}
        />
        <HookTextarea name='note' label='Delegation Note' />
      </div>
      <div className='flex gap-2 justify-end w-full mt-5'>
        <Button label='Cancel' btnStyle='btn-neutral' btnType='button' disabled={updateApprovalMutation.isLoading} onClick={onCancel} />
        <Button
          label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Delegate'}`}
          btnStyle='btn-primary'
          btnType='submit'
          disabled={updateApprovalMutation.isLoading}
        />
      </div>
    </HookForm>
  );
}

export const DelegateModal = NiceModal.create(
  ({ approvalId, assignedUser, projectId, onSubmitted, title }: Omit<DelegateModalProps, 'onCancel'>) => {
    const { visible, hide } = useModal();

    if (!visible) return null;

    return (
      <Modal>
        <HookFormProvider>
          <DelegateHookForm
            title={title}
            projectId={projectId}
            approvalId={approvalId}
            assignedUser={assignedUser}
            onCancel={hide}
            onSubmitted={onSubmitted}
          />
        </HookFormProvider>
      </Modal>
    );
  }
);
